import React, { useEffect, useRef, useState } from 'react';
import { func, string } from 'prop-types';

import { injectIntl } from '../../../util/reactIntl';

import css from './PriceInputs.module.css';

const COMMIT_DEBOUNCE_MS = 600;

/**
 * Price min/max inputs (ledger "Filter system — FINAL 2026-08-25", SUPERSEDES
 * quick chips): two inputs 50/50 with an en-dash separator, "$ min"/"$ max"
 * placeholders, NO Apply button (auto-applies like every other filter), no
 * quick-amount chips, no slider. Commits on blur/Enter and after a typing
 * pause. Shared by the price pill dropdown and the panel's Price section.
 */
const PriceInputsComponent = props => {
  const { value, onCommit, intl } = props;
  const [minRaw, maxRaw] = (value || ',').split(',');
  const [min, setMin] = useState(minRaw || '');
  const [max, setMax] = useState(maxRaw || '');
  const timerRef = useRef(null);

  // External changes (Clear all, back/forward) re-seed the inputs
  useEffect(() => {
    setMin(minRaw || '');
    setMax(maxRaw || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = (minValue, maxValue) => {
    const cleanMin = String(minValue).trim();
    const cleanMax = String(maxValue).trim();
    if (!cleanMin && !cleanMax) {
      onCommit(null);
      return;
    }
    // The listings query needs both bounds — default the missing one
    const minNum = cleanMin === '' ? 0 : parseInt(cleanMin, 10);
    const maxNum = cleanMax === '' ? 25000 : parseInt(cleanMax, 10);
    if (Number.isNaN(minNum) || Number.isNaN(maxNum)) return;
    onCommit(`${minNum},${maxNum}`);
  };

  const scheduleCommit = (minValue, maxValue) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => commit(minValue, maxValue), COMMIT_DEBOUNCE_MS);
  };

  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);

  const inputProps = (val, setter, other, isMin) => ({
    type: 'number',
    inputMode: 'numeric',
    min: 0,
    className: css.input,
    value: val,
    placeholder: intl.formatMessage({
      id: isMin ? 'PriceInputs.minPlaceholder' : 'PriceInputs.maxPlaceholder',
    }),
    onChange: e => {
      setter(e.target.value);
      scheduleCommit(isMin ? e.target.value : other, isMin ? other : e.target.value);
    },
    onBlur: () => commit(isMin ? val : other, isMin ? other : val),
    onKeyDown: e => {
      if (e.key === 'Enter') commit(isMin ? val : other, isMin ? other : val);
    },
  });

  return (
    <div className={css.root}>
      <input {...inputProps(min, setMin, max, true)} />
      <span className={css.separator}>–</span>
      <input {...inputProps(max, setMax, min, false)} />
    </div>
  );
};

PriceInputsComponent.defaultProps = {
  value: null,
};

PriceInputsComponent.propTypes = {
  value: string,
  onCommit: func.isRequired,
};

export default injectIntl(PriceInputsComponent);
